// Single source of truth for the demo. All UI mutations go through dispatch();
// the reducer keeps fund-coverage denormalizations consistent so the project
// page banner (已资金覆盖 / 未资金覆盖) is always accurate.
//
// Nothing here hits any backend. When we wire the real version, the action
// types in this reducer become the API endpoints.

import React, { createContext, useContext, useMemo, useReducer } from 'react';
import type {
  ContractV2,
  ContractV2Bundle,
  ContractAcceptanceState,
  Milestone,
  PaymentPlanEntry,
  ReleasePlanEntry,
  ContractChange,
  ContractDispute,
  ProjectType,
  PartyInfo,
  AccountType,
  FundCoverageView,
} from './mockTypes';
import {
  ACCEPTANCE_PERIOD_DEFAULTS,
  ACCEPTANCE_PERIOD_COMPANY_BONUS,
} from './mockTypes';
import { seedBundles, DEMO_VIEWER_ID } from './mockData';

// =====================================================================
// Action types — stable enough that we can copy them into a real API contract.
// =====================================================================

export type Action =
  | { type: 'CREATE_BUNDLE'; bundle: ContractV2Bundle }
  | { type: 'ACCEPT_CONTRACT'; contractId: string }
  | { type: 'REJECT_CONTRACT'; contractId: string }
  | { type: 'CLIENT_MARK_PAID'; contractId: string; paymentEntryId: string }
  | { type: 'PLATFORM_CONFIRM_PAYMENT'; contractId: string; paymentEntryId: string }
  | { type: 'CREATOR_SUBMIT_DELIVERY'; contractId: string; milestoneId: string; assets: Milestone['submittedAssets']; validityPassed: boolean; validityIssues?: string[] }
  | { type: 'CLIENT_ACCEPT_MILESTONE'; contractId: string; milestoneId: string }
  | { type: 'CLIENT_REQUEST_REVISION'; contractId: string; milestoneId: string; reason: string; revisionDueAt: number; category: 'detail' | 'invalid_delivery' }
  | { type: 'CLIENT_PROPOSE_CHANGE'; contractId: string; milestoneId: string; scopeDelta: string; feeDelta: number; additionalDays: number }
  | { type: 'AGREE_CHANGE'; contractId: string; changeId: string }
  | { type: 'REJECT_CHANGE'; contractId: string; changeId: string }
  | { type: 'CREATOR_PAUSE_WORK'; contractId: string; milestoneId: string } // visual only, no state field flips
  | { type: 'AUTO_ACCEPT_TIMER_FIRED'; contractId: string; milestoneId: string }
  | { type: 'RAISE_DISPUTE'; contractId: string; raisedBy: 'client' | 'creator'; reason: string }
  | { type: 'SET_VIEWER'; viewerId: string };

// =====================================================================
// Selectors
// =====================================================================

export function selectFundCoverage(bundle: ContractV2Bundle): FundCoverageView {
  const { contract, releasePlan } = bundle;
  const availableBalance = contract.totalPaid - contract.totalReleased - contract.totalRefunded;

  let cumulative = 0;
  const perMilestone = releasePlan
    .slice()
    .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
    .map(rp => {
      cumulative += rp.amount;
      return {
        milestoneId: rp.milestoneId,
        cumulativeReleaseRequired: cumulative,
        // Covered if: paid-so-far minus released-so-far is enough to fund this entry.
        // (Tail is excluded from totalPaid until tail-pay actually posts; this is the
        // mechanic the spec describes as "尾款未到账前不视为已托管".)
        isCovered: contract.totalPaid - contract.totalReleased >= cumulative,
      };
    });

  return {
    totalPaid: contract.totalPaid,
    totalReleased: contract.totalReleased,
    totalRefunded: contract.totalRefunded,
    availableBalance,
    tailAmount: contract.tailAmount,
    perMilestone,
  };
}

export function isMilestoneAssetCovered(bundle: ContractV2Bundle, milestoneId: string): boolean {
  const cov = selectFundCoverage(bundle);
  return cov.perMilestone.find(m => m.milestoneId === milestoneId)?.isCovered ?? false;
}

export function computeAcceptancePeriodDays(projectType: ProjectType, buyerAccountType: AccountType): number {
  const base = ACCEPTANCE_PERIOD_DEFAULTS[projectType];
  return base + (buyerAccountType === 'company' ? ACCEPTANCE_PERIOD_COMPANY_BONUS : 0);
}

// =====================================================================
// Reducer
// =====================================================================

interface State {
  bundles: Record<string, ContractV2Bundle>;
  viewerId: string;
}

function initialState(): State {
  const bundles: Record<string, ContractV2Bundle> = {};
  for (const b of seedBundles) bundles[b.contract.id] = b;
  return { bundles, viewerId: DEMO_VIEWER_ID };
}

function withContract(state: State, contractId: string, fn: (b: ContractV2Bundle) => ContractV2Bundle): State {
  const bundle = state.bundles[contractId];
  if (!bundle) return state;
  return { ...state, bundles: { ...state.bundles, [contractId]: fn(bundle) } };
}

function recomputeTotals(b: ContractV2Bundle): ContractV2Bundle {
  // totalPaid = sum of platform-confirmed entries, EXCLUDING tail until tail itself posts.
  // The tail entry is identified by isFinalTail; its amount only counts after it posts.
  const totalPaid = b.paymentPlan
    .filter(p => p.status === 'PLATFORM_CONFIRMED')
    .reduce((s, p) => s + p.amount, 0);
  const totalReleased = b.releasePlan
    .filter(r => r.status === 'RELEASED')
    .reduce((s, r) => s + r.amount, 0);
  return {
    ...b,
    contract: {
      ...b.contract,
      totalPaid,
      totalReleased,
    },
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_VIEWER':
      return { ...state, viewerId: action.viewerId };

    case 'CREATE_BUNDLE':
      return { ...state, bundles: { ...state.bundles, [action.bundle.contract.id]: action.bundle } };

    case 'ACCEPT_CONTRACT':
      return withContract(state, action.contractId, b => ({
        ...b,
        contract: {
          ...b.contract,
          acceptanceState: 'active' as ContractAcceptanceState,
          signedAt: Date.now(),
          accountTypeAtSign: b.contract.client.accountType,
        },
      }));

    case 'REJECT_CONTRACT':
      return withContract(state, action.contractId, b => ({
        ...b,
        contract: { ...b.contract, acceptanceState: 'rejected' as ContractAcceptanceState },
      }));

    case 'CLIENT_MARK_PAID':
      return withContract(state, action.contractId, b => recomputeTotals({
        ...b,
        paymentPlan: b.paymentPlan.map(p =>
          p.id === action.paymentEntryId
            ? { ...p, status: 'PAID_BY_CLIENT', paidAt: Date.now() }
            : p
        ),
      }));

    case 'PLATFORM_CONFIRM_PAYMENT':
      return withContract(state, action.contractId, b => recomputeTotals({
        ...b,
        paymentPlan: b.paymentPlan.map(p =>
          p.id === action.paymentEntryId
            ? { ...p, status: 'PLATFORM_CONFIRMED', platformConfirmedAt: Date.now() }
            : p
        ),
        // Mark milestones funded based on the new totalPaid - totalReleased delta
        milestones: b.milestones,
      }));

    case 'CREATOR_SUBMIT_DELIVERY': {
      const bundle = state.bundles[action.contractId];
      if (!bundle) return state;
      const period = computeAcceptancePeriodDays(bundle.contract.projectType, bundle.contract.client.accountType);
      const now = Date.now();
      return withContract(state, action.contractId, b => ({
        ...b,
        milestones: b.milestones.map(m =>
          m.id === action.milestoneId
            ? {
                ...m,
                submittedAssets: action.assets,
                lastDeliveryAt: now,
                validityCheckPassed: action.validityPassed,
                validityIssues: action.validityIssues,
                // If the validity check passed, flip to IN_ACCEPTANCE and start
                // the 验收期 timer. Otherwise stay in SUBMITTED so creator can retry.
                status: action.validityPassed ? 'IN_ACCEPTANCE' : 'SUBMITTED',
                acceptancePeriodEndsAt: action.validityPassed ? now + period * 24 * 3600 * 1000 : undefined,
                // Mark every open revision request as resolved by this delivery.
                revisionHistory: (m.revisionHistory || []).map(r =>
                  r.resolvedAt ? r : { ...r, resolvedAt: now }
                ),
              }
            : m
        ),
      }));
    }

    case 'CLIENT_ACCEPT_MILESTONE': {
      return withContract(state, action.contractId, b => {
        const milestone = b.milestones.find(m => m.id === action.milestoneId);
        if (!milestone) return b;
        // Find the matching release entry, mark released.
        const release = b.releasePlan.find(r => r.milestoneId === action.milestoneId);
        const next: ContractV2Bundle = {
          ...b,
          milestones: b.milestones.map(m =>
            m.id === action.milestoneId ? { ...m, status: 'COMPLETED', paymentStatus: 'RELEASED' } : m
          ),
          releasePlan: release
            ? b.releasePlan.map(r =>
                r.id === release.id ? { ...r, status: 'RELEASED', releasedAt: Date.now() } : r
              )
            : b.releasePlan,
        };
        return recomputeTotals(next);
      });
    }

    case 'CLIENT_REQUEST_REVISION':
      return withContract(state, action.contractId, b => ({
        ...b,
        milestones: b.milestones.map(m =>
          m.id === action.milestoneId
            ? {
                ...m,
                status: 'REVISION_REQUESTED',
                // 无效交付 does NOT consume a revision slot — it's
                // signalling the prior delivery wasn't valid, so the
                // creator owes the work regardless. Only 细节优化
                // counts toward the limit.
                revisionCountUsed: action.category === 'detail'
                  ? m.revisionCountUsed + 1
                  : m.revisionCountUsed,
                revisionDueAt: action.revisionDueAt,
                acceptancePeriodEndsAt: undefined,
                revisionHistory: [
                  ...(m.revisionHistory || []),
                  {
                    id: `rev_${Math.random().toString(36).slice(2, 10)}`,
                    requestedAt: Date.now(),
                    reason: action.reason,
                    returnDueAt: action.revisionDueAt,
                    category: action.category,
                  },
                ],
              }
            : m
        ),
      }));

    case 'CLIENT_PROPOSE_CHANGE': {
      const id = `chg_${Math.random().toString(36).slice(2, 10)}`;
      const change: ContractChange = {
        id,
        contractId: action.contractId,
        proposedBy: 'client',
        proposedAt: Date.now(),
        scopeDelta: action.scopeDelta,
        feeDelta: action.feeDelta,
        additionalDays: action.additionalDays,
        status: 'PENDING_AGREE',
      };
      return withContract(state, action.contractId, b => ({
        ...b,
        changes: [...b.changes, change],
      }));
    }

    case 'AGREE_CHANGE':
      return withContract(state, action.contractId, b => ({
        ...b,
        changes: b.changes.map(c =>
          c.id === action.changeId ? { ...c, status: 'AGREED', resolvedAt: Date.now() } : c
        ),
      }));

    case 'REJECT_CHANGE':
      return withContract(state, action.contractId, b => ({
        ...b,
        changes: b.changes.map(c =>
          c.id === action.changeId ? { ...c, status: 'REJECTED', resolvedAt: Date.now() } : c
        ),
      }));

    case 'AUTO_ACCEPT_TIMER_FIRED':
      // Same effect as CLIENT_ACCEPT_MILESTONE — just routed through a different
      // path so the demo can show "客户超期未操作 → 系统自动验收并放款".
      return reducer(state, { type: 'CLIENT_ACCEPT_MILESTONE', contractId: action.contractId, milestoneId: action.milestoneId });

    case 'RAISE_DISPUTE': {
      const id = `dis_${Math.random().toString(36).slice(2, 10)}`;
      const dispute: ContractDispute = {
        id,
        contractId: action.contractId,
        raisedBy: action.raisedBy,
        raisedAt: Date.now(),
        reason: action.reason,
        status: 'OPEN',
      };
      return withContract(state, action.contractId, b => ({
        ...b,
        disputes: [...b.disputes, dispute],
        contract: { ...b.contract, acceptanceState: 'disputed' as ContractAcceptanceState },
      }));
    }

    default:
      return state;
  }
}

// =====================================================================
// Context
// =====================================================================

interface ContractsV2ContextValue {
  bundles: ContractV2Bundle[];
  bundleById(id: string): ContractV2Bundle | undefined;
  viewerId: string;
  isViewerClient(bundle: ContractV2Bundle): boolean;
  isViewerCreator(bundle: ContractV2Bundle): boolean;
  dispatch: React.Dispatch<Action>;
}

const Ctx = createContext<ContractsV2ContextValue | null>(null);

export function ContractsV2Provider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  const value = useMemo<ContractsV2ContextValue>(() => {
    const bundlesArr = Object.values(state.bundles);
    return {
      bundles: bundlesArr,
      bundleById: (id) => state.bundles[id],
      viewerId: state.viewerId,
      isViewerClient: (b) => b.contract.client.userId === state.viewerId,
      isViewerCreator: (b) => b.contract.creator.userId === state.viewerId,
      dispatch,
    };
  }, [state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useContractsV2(): ContractsV2ContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useContractsV2 must be inside ContractsV2Provider');
  return v;
}
